/**
 * The back-step rule, as pure state.
 *
 * A respondent may go back exactly one question, and no further. Once they
 * move forward again, that back step is spent for that question: they cannot
 * return to it a second time. There is no skip control anywhere, and a
 * respondent never moves to a question they have not already been shown.
 *
 * Positions are 0-based indexes into the 17 questions. `floor` is the index
 * the current session began at: a resumed session cannot step back into a
 * question answered in an earlier session, because those selections were never
 * stored client side (spec FR5: localStorage holds the response id and first
 * name only) and the server does not return them (spec FR10). Going back onto
 * a blank, already-answered question and being forced to re-answer it would be
 * worse than not offering the step at all.
 *
 * Kept free of React so the rule can be tested directly, like selection.ts.
 */

export interface Navigation {
  /** Index of the question on screen. */
  readonly index: number;
  /** Lowest index this session may show. 0 for a fresh start. */
  readonly floor: number;
  /** True when the current question was reached by going back. One step only. */
  readonly arrivedByBack: boolean;
  /** Indexes from which the single back step has already been used. */
  readonly spent: ReadonlySet<number>;
}

export function startNavigation(index: number): Navigation {
  return { index, floor: index, arrivedByBack: false, spent: new Set() };
}

/**
 * Back is offered only when all of these hold:
 *   - there is a previous question in this session (index > floor),
 *   - the respondent moved here forwards, not by a back step, and
 *   - the back step from this question has not already been spent.
 */
export function canGoBack(nav: Navigation): boolean {
  return nav.index > nav.floor && !nav.arrivedByBack && !nav.spent.has(nav.index);
}

/** Steps back one question and spends this question's back step. */
export function goBack(nav: Navigation): Navigation {
  if (!canGoBack(nav)) return nav;
  const spent = new Set(nav.spent);
  spent.add(nav.index);
  return { ...nav, index: nav.index - 1, arrivedByBack: true, spent };
}

/** Moves forward one question. Clears the "arrived by back" mark. */
export function goForward(nav: Navigation, total: number): Navigation {
  if (nav.index + 1 >= total) return nav;
  return { ...nav, index: nav.index + 1, arrivedByBack: false };
}
